import type { GitHubService } from './GitHubService';
import type { NavOrderFile } from '../types';
import {
  isGitHubWrite409,
  parseBlobShaHintFromGithub409,
  sleep,
} from '../utils/githubWriteErrors';
import { sanitizeNavOrder } from '../utils/navOrder';
import { makeNavOrderFile, sanitizeNavIcons } from '../utils/pageIcon';

export const NAV_ORDER_PATH = 'database/.dailylife-nav-order.json';

export class NavOrderService {
  private file: NavOrderFile = { version: 2, order: {} };
  private sha = '';
  private persistChain: Promise<void> = Promise.resolve();

  constructor(private readonly github: GitHubService) {}

  getNavOrder(): NavOrderFile {
    return this.file;
  }

  setNavOrder(next: NavOrderFile): void {
    this.file = next;
  }

  async load(): Promise<void> {
    try {
      const { content, sha } = await this.github.readFile(NAV_ORDER_PATH);
      const parsed = JSON.parse(content) as { order?: unknown; icons?: unknown };
      this.file = makeNavOrderFile(sanitizeNavOrder(parsed?.order), sanitizeNavIcons(parsed?.icons));
      this.sha = sha;
    } catch {
      this.file = { version: 2, order: {} };
      this.sha = '';
    }
  }

  async persist(): Promise<void> {
    const run = async (): Promise<void> => {
      const maxAttempts = 6;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const body = JSON.stringify(this.file, null, 2);
        let sha = '';
        try {
          sha = (await this.github.readFile(NAV_ORDER_PATH)).sha;
        } catch {
          sha = '';
        }

        const commit = async (s: string): Promise<void> => {
          const newSha = await this.github.writeFile(NAV_ORDER_PATH, body, s, 'DailyLife: sidebar order');
          this.sha = newSha ?? (await this.github.readFile(NAV_ORDER_PATH)).sha;
        };

        try {
          await commit(sha);
          return;
        } catch (e) {
          if (!isGitHubWrite409(e)) throw e;
          const hint = parseBlobShaHintFromGithub409(e);
          if (hint && hint !== sha.toLowerCase()) {
            try {
              await commit(hint);
              return;
            } catch (e2) {
              if (!isGitHubWrite409(e2)) throw e2;
            }
          }
          if (attempt >= maxAttempts - 1) throw e;
          await sleep(80 * 2 ** attempt);
        }
      }
    };

    const p = this.persistChain.then(run);
    this.persistChain = p.catch(() => {});
    return p;
  }
}
